import { IdentityProvider, Subscription } from '@localstack/types';

import { toSortedSafe } from '~/util/array';

import { SubscriptionGroupEntry } from './types';

/**
 * Wrap the metadata field's value so it can be read as a data URI.
 *
 * The field holds a `File` after an upload, but the XML string Cognito returned when the provider
 * was loaded. Casting the latter to a `File` is what made saving throw inside `FileReader`.
 */
export const toMetadataBlob = (value: File | string): Blob =>
  value instanceof File ? value : new Blob([value], { type: 'text/xml' });

export const blobToBase64 = (blob: Blob): Promise<string | undefined> =>
  new Promise<string | undefined>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => resolve(reader.result as string | undefined);
    reader.onerror = (error) => reject(error);
  });

/**
 * Removes empty-string values from an attribute mapping.
 * Returns a plain string->string map (no empty values).
 */
export const cleanAttributeMapping = (mapping: IdentityProvider['attribute_mapping']): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(mapping || {}).filter(([, value]) => typeof value === 'string' && value.trim() !== ''),
  ) as Record<string, string>;
};

/**
 * Build an array of subscription entries for the SCIM dialog.
 *
 * The resulting array is **sorted by creation time (newest → oldest)** to ensure
 * the most recent subscriptions appear first. Sorting is performed via
 * {@link toSortedSafe | toSortedSafe}, which uses `Array.prototype.toSorted`
 * when available and falls back to a non-mutating copy + `sort()` in older environments.
 *
 * Each entry preserves duplicates (same name) and carries enough data for the UI
 * to disambiguate by date and, when needed, by subscription id.
 *
 * - `name`   (display base): "<plan.name> (<PLAN.EMULATOR_UPPERCASE>)"
 * - `group`  (backend id):   "LocalStack-<plan.name>-<PLAN.EMULATOR_UPPERCASE>-<subscription.id>"
 * - `id`     (subscription id)
 * - `created` (UNIX seconds)
 *
 * The UI can then render labels like:
 *   "<name> — YYYY-MM-DD" and append " · <id>" only when multiple entries share the same name & date.
 *
 * @param subs - List of subscriptions to include.
 * @returns Array of {@link SubscriptionGroupEntry}, newest first.
 * @see {@link toSortedSafe} (from `~/util/array`)
 */
export const buildSubscriptionGroups = (subs: Subscription[]): SubscriptionGroupEntry[] => {
  const compareByCreatedDesc = (a: Subscription, b: Subscription) => b.created - a.created;
  const sorted = toSortedSafe(subs, compareByCreatedDesc);

  return sorted.map((sub) => {
    const planName = sub.plan.name;
    const emulator = sub.plan.emulator.toUpperCase();
    const name = `${planName} (${emulator})`;
    const group = `LocalStack-${planName}-${emulator}-${sub.id}`;

    return {
      id: sub.id,
      name,
      group,
      created: sub.created, // seconds
    };
  });
};
